import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useDocumentsBateaux, TypeDocument, DocumentBateau } from '@/hooks/useDocumentsBateaux';
import { FileText, Upload, Trash2, Eye, Download, Cloud } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';
import DocumentPreviewDialog from '@/components/bateaux/DocumentPreviewDialog';
import { useTranslation } from 'react-i18next';

interface DocumentsDialogProps {
  open: boolean;
  onClose: () => void;
  bateauId: string;
  bateauNom: string;
}

const DocumentsDialog = ({ open, onClose, bateauId, bateauNom }: DocumentsDialogProps) => {
  const { t } = useTranslation('boats');
  const { 
    documents, 
    loading, 
    uploadDocument, 
    supprimerDocument, 
    downloadDocument,
    getDocumentUrl
  } = useDocumentsBateaux(bateauId);
  const [uploadingType, setUploadingType] = useState<TypeDocument | null>(null);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<DocumentBateau | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const fileInputRefs = {
    carte_navigation: useRef<HTMLInputElement>(null),
    attestation_assurance: useRef<HTMLInputElement>(null),
    permis_navigation: useRef<HTMLInputElement>(null),
  };

  const documentTypes: Record<TypeDocument, { label: string; description: string }> = {
    carte_navigation: {
      label: t('documentsDialog.types.carte_navigation.label'),
      description: t('documentsDialog.types.carte_navigation.description'),
    },
    attestation_assurance: {
      label: t('documentsDialog.types.attestation_assurance.label'),
      description: t('documentsDialog.types.attestation_assurance.description'),
    },
    permis_navigation: {
      label: t('documentsDialog.types.permis_navigation.label'),
      description: t('documentsDialog.types.permis_navigation.description'),
    },
  };

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
    type: TypeDocument
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingType(type);
    await uploadDocument(file, type);
    setUploadingType(null);

    // Réinitialiser l'input
    if (event.target) {
      event.target.value = '';
    }
  };

  const handleView = async (doc: DocumentBateau) => {
    // Ne pas ouvrir un nouvel onglet (popups/anti-tracking) : prévisualiser dans l'app.
    setPreviewDoc(doc);
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewUrl(null);

    try {
      const blob = await downloadDocument(doc);
      if (blob) {
        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);
        return;
      }

      // Fallback : URL signée
      const signedUrl = await getDocumentUrl(doc.chemin_stockage);
      if (signedUrl) {
        setPreviewUrl(signedUrl);
        return;
      }

      toast({
        title: t('documentsDialog.errorTitle'),
        description: t('documentsDialog.errorView'),
        variant: 'destructive',
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  // Nettoyage des URLs blob
  useEffect(() => {
    if (!previewOpen && previewUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  }, [previewOpen, previewUrl]);

  const handleDownload = async (doc: DocumentBateau) => {
    const blob = await downloadDocument(doc);
    if (blob) {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.nom_fichier;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } else {
      toast({
        title: t('documentsDialog.errorTitle'),
        description: t('documentsDialog.errorDownload'),
        variant: "destructive",
      });
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getExistingDocument = (type: TypeDocument) => {
    return documents.find(doc => doc.type_document === type);
  };

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-nautic-primary">
            <FileText className="h-5 w-5" />
            {t('documentsDialog.titlePrefix')} {bateauNom}
          </DialogTitle>
          <DialogDescription>
            {t('documentsDialog.description')}
          </DialogDescription>
        </DialogHeader>

        {/* Message d'information sur le stockage cloud */}
        <div className="bg-nautic-primary/10 border border-nautic-primary/20 rounded-lg p-4 flex items-center gap-3">
          <Cloud className="h-5 w-5 text-nautic-primary flex-shrink-0" />
          <p className="text-sm text-foreground">
            {t('documentsDialog.cloudInfo')}
          </p>
        </div>

        <div className="space-y-6">
          {(Object.keys(documentTypes) as TypeDocument[]).map((type) => {
            const info = documentTypes[type];
            const existingDoc = getExistingDocument(type);
            const isUploading = uploadingType === type;

            return (
              <Card key={type}>
                <CardHeader>
                  <CardTitle className="text-lg">{info.label}</CardTitle>
                  <p className="text-sm text-muted-foreground">{info.description}</p>
                </CardHeader>
                <CardContent>
                  {existingDoc ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <FileText className="h-8 w-8 text-nautic-primary" />
                          <div>
                            <p className="font-medium">{existingDoc.nom_fichier}</p>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <span>{formatFileSize(existingDoc.taille_fichier)}</span>
                              <Badge variant="outline">
                                {existingDoc.type_mime.includes('pdf') ? t('documentsDialog.pdf') : t('documentsDialog.image')}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleView(existingDoc)}
                            disabled={loading}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownload(existingDoc)}
                            disabled={loading}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => supprimerDocument(existingDoc.id)}
                            disabled={loading}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          onClick={() => fileInputRefs[type].current?.click()}
                          disabled={isUploading || loading}
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          {isUploading ? t('documentsDialog.replacing') : t('documentsDialog.replace')}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center">
                        <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground mb-4">{t('documentsDialog.noDocument')}</p>
                        <Button
                          onClick={() => fileInputRefs[type].current?.click()}
                          disabled={isUploading || loading}
                          className="bg-nautic-primary hover:bg-nautic-primary/90 text-white"
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          {isUploading ? t('documentsDialog.uploading') : t('documentsDialog.upload')}
                        </Button>
                      </div>
                    </div>
                  )}

                  <input
                    ref={fileInputRefs[type]}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileSelect(e, type)}
                    className="hidden"
                  />
                </CardContent>
              </Card>
            );
          })}
        </div>

        <DocumentPreviewDialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          title={previewDoc ? t('documentsDialog.previewTitle', { fileName: previewDoc.nom_fichier }) : 'Document'}
          description={previewDoc ? `${bateauNom} • ${previewDoc.type_document}` : bateauNom}
          fileName={previewDoc?.nom_fichier}
          mimeType={previewDoc?.type_mime}
          url={previewUrl}
          loading={previewLoading}
        />

        <div className="flex justify-end pt-4">
          <Button variant="outline" onClick={onClose}>
            {t('documentsDialog.close')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentsDialog;
